import { useRouter } from 'expo-router'

import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { buildJoinSteps } from '@/src/features/community/utils/buildJoinSteps'
import { getJoinStepperOptions } from '@/src/features/community/utils/getJoinStepperOptions'
import { getNextJoinStep } from '@/src/features/community/utils/getNextJoinStep'
import { getPreviousJoinStep } from '@/src/features/community/utils/getPreviousJoinStep'
import { useFraternityOnboardingQuery } from '@/src/features/fraternity/queries/useFraternityOnboardingQuery'
import { withFraternity } from '@/src/features/fraternity/utils/withFraternity'
import { useIsPushGranted } from '@/src/features/notification/ui/hooks/useIsPushGranted'
import type { TFraternityJoinParams } from '@/src/general/constants/routes'
import { routeToPath } from '@/src/general/constants/routes'
import { useOnChange } from '@/src/general/hooks/useOnChange'
import { filterSearchParams } from '@/src/general/utils/filterSearchParams'
import { parseIdDash } from '@/src/general/utils/parseIdDash'
import { useRoutes } from '@/src/general/utils/routes'

import type { TJoinDestination, TJoinStep } from '../../models/joinSteps.types'
import { useCommunityQuery } from '../../services/useCommunityQuery'
import { useGetCommunityJoinRoute } from './useGetCommunityJoinRoute'

type TUseCommunityJoinStepsParams = {
  stepName: TJoinStep
} & TIdSlugParam &
  TSearchParamsParam

export const useCommunityJoinSteps = ({ stepName, idSlug, searchParams }: TUseCommunityJoinStepsParams) => {
  const router = useRouter()
  const { routes } = useRoutes()
  const getCommunityJoinRoute = useGetCommunityJoinRoute()

  const [id, slug] = idSlug ? parseIdDash(idSlug) : []
  const { course, origin, source, fromRecap } = filterSearchParams<TFraternityJoinParams>(searchParams)
  const isEditingFromRecap = fromRecap === 'true'

  const { data: community, isPending: isCommunityPending } = useCommunityQuery(id)
  const { data: me, isPending: isMePending } = useMeQuery()

  const isWithFraternity = withFraternity(community)
  const { isPushGranted, isPending: isPushPermissionPending } = useIsPushGranted({ enabled: isWithFraternity })

  const { data: onboarding, isPending: isOnboardingPending } = useFraternityOnboardingQuery({
    community,
    enabled: isWithFraternity,
  })

  const joinSteps = buildJoinSteps({ community, me, isPushGranted, onboarding })

  const isPending =
    isCommunityPending || isMePending || (isWithFraternity && (isOnboardingPending || isPushPermissionPending))

  const stepperOptions = getJoinStepperOptions({ stepName, joinSteps, community })

  const joinRouteParams = { id: id ?? 0, slug: slug ?? '', course, origin, source }

  const navigateToDestination = (destination: TJoinDestination) => {
    if (destination === 'community') {
      if (!id || !slug) return
      router.dismissTo(routeToPath(routes.community({ id, slug, origin })))
    } else {
      router.navigate(getCommunityJoinRoute(destination, joinRouteParams))
    }
  }

  const goToPreviousStep = () => navigateToDestination(getPreviousJoinStep(stepName, joinSteps))
  const goToNextStep = () => {
    if (isEditingFromRecap) {
      router.dismissTo(routeToPath(routes.communityJoinRecap(joinRouteParams)))
    } else {
      navigateToDestination(getNextJoinStep(stepName, joinSteps, { isVirtus: isWithFraternity }))
    }
  }

  const goToClose = () => {
    if (!id || !slug) return
    router.dismissTo(routeToPath(routes.community({ id, slug, origin })))
  }

  const navigateToJoinStep = (targetStep: TJoinStep, options?: { fromRecap?: boolean }) => {
    if (!id || !slug) return
    router.navigate(
      getCommunityJoinRoute(targetStep, { ...joinRouteParams, ...(options?.fromRecap ? { fromRecap: 'true' } : {}) }),
    )
  }

  const shouldBeDisplayed = isPending || joinSteps.includes(stepName)
  const shouldGoToNextStep = !isPending && !shouldBeDisplayed

  useOnChange(shouldGoToNextStep, () => {
    if (shouldGoToNextStep) goToNextStep()
  })

  return {
    goToClose,
    goToNextStep,
    goToPreviousStep,
    isPending,
    navigateToJoinStep,
    shouldBeDisplayed,
    stepperOptions,
  }
}
